/**
 * The Actions button over a list (texts, words): a menu of what can be done
 * with the items selected (ui/Menu: iOS's own in the app). It lists the
 * actions applying to the selection, split into sections where the list has
 * a splitter.
 */
import { effect } from "@preact/signals";
import * as preact from "preact";

import SelectionActionList, { SelectionAction } from "SelectionActionList";
import SelectionStore from "stores/SelectionStore";
import Button from "ui/Button";
import Menu, { type MenuSection } from "ui/Menu";

interface Props {
  model: SelectionStore;
  selectionActionList: SelectionActionList;
  name?: string;
  itemIDsOnly?: boolean;
}

class SelectionActionView extends preact.Component<Props> {
  private disposeEffect?: () => void;

  componentDidMount() {
    this.disposeEffect = effect(() => {
      void this.props.model.count.value;
      void this.props.model.itemIDs.value;
      void this.props.model.wholeCollection.value;
      void this.props.model.wholePage.value;
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.disposeEffect?.();
  }

  private perform(action: SelectionAction) {
    if (action.exec) {
      void action.exec();
    } else {
      this.props.model.performAction(action);
    }
  }

  /** The actions applying to the selection, named for one item or several */
  private sections(): MenuSection[] {
    const { model, itemIDsOnly } = this.props;
    const single = model.itemIDs.value.length === 1;
    const sections: MenuSection[] = [{ items: [] }];
    for (const action of this.props.selectionActionList.actions) {
      if (action.splitter) {
        sections.push({ items: [] });
      } else if (
        action.appliesToSelection(model, { itemIDsOnly }) &&
        (single || !action.singleSelectionOnly)
      ) {
        sections[sections.length - 1].items.push({
          label:
            (single ? action.name : action.namePlural) ?? action.name ?? "",
          destructive: action.data.destructive,
          onSelect: () => this.perform(action),
        });
      }
    }
    return sections;
  }

  render() {
    return (
      <Menu
        class="actions"
        align="start"
        sections={this.sections()}
        renderButton={(buttonProps) => (
          <Button
            {...buttonProps}
            kind="primary"
            chevron="down"
            class="actionsButton"
            disabled={this.props.model.count.value === 0}
          >
            {this.props.name ?? "Actions"}
          </Button>
        )}
      />
    );
  }
}

export default SelectionActionView;
